import './LoiPreparationModal.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { useStateSelector } from '../../../store/selectors';
import { fetchLoiPreparationUrl, fetchLoiPreparationStatus, fetchLoiCoOwnersPreview } from '../../../slices/marketplace/loi-preparation/loi-preparation-api';
import {
    LoiCoOwner,
    LoiCoOwnerIssue,
    LoiCoOwnersPreview,
    LoiFellowOwner,
    LoiPreparationRequest,
    LoiSigningMode,
} from '../../../slices/marketplace/loi-preparation/models/loi-co-owners-preview.model';
import Helpers from '../../../utils/helper';
import createNotification from '../../../utils/createNotification';

const LOI_PREPARATION_MODAL_WIDTH = 800;

const CO_OWNER_ISSUE_TEXT: Record<LoiCoOwnerIssue, string> = {
    [LoiCoOwnerIssue.None]: '',
    [LoiCoOwnerIssue.MissingEmail]: 'email is missing',
    [LoiCoOwnerIssue.InvalidEmail]: 'email is not valid',
    [LoiCoOwnerIssue.DuplicateEmail]: 'email is already used in another row',
    [LoiCoOwnerIssue.MissingName]: 'name is missing',
};

const SIGNING_MODE_OPTIONS = [
    { value: LoiSigningMode.InVetValue, label: 'In VetValue' },
    { value: LoiSigningMode.ByEmail, label: 'By email' },
];

interface FellowOwnerState {
    isSelected: boolean;
    mode: LoiSigningMode;
}

type FellowOwnerStateById = Record<number, FellowOwnerState>;

interface FellowOwnerExtraData {
    fellowOwnerStateById: FellowOwnerStateById;
    isLocked: boolean;
}

const buildFellowOwnerState = (fellowOwners: LoiFellowOwner[]): FellowOwnerStateById => {
    const stateById: FellowOwnerStateById = {};
    fellowOwners.forEach((fellowOwner) => {
        stateById[fellowOwner.associateMemberId] = {
            isSelected: fellowOwner.isSelected,
            mode: fellowOwner.hasAccount ? fellowOwner.mode : LoiSigningMode.ByEmail,
        };
    });
    return stateById;
};

const DEFAULT_PREPARATION_ERROR = 'Could not load LOI preparation. Please try again.';
const CO_OWNERS_LOAD_ERROR = 'Could not load the LOI co-owners. Close this pop-up and try again.';

const getPreparationErrorMessage = (err: unknown): string =>
    err instanceof Error && err.message ? err.message : DEFAULT_PREPARATION_ERROR;

export interface LoiPreparationModalProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    eventId: string;
    buyerId: number;
}

export const LoiPreparationModal = (props: LoiPreparationModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [prepareUrl, setPrepareUrl] = useState<string | null>(null);
    const [preview, setPreview] = useState<LoiCoOwnersPreview | null>(null);
    const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
    const [primaryOwnerMode, setPrimaryOwnerMode] = useState<LoiSigningMode>(LoiSigningMode.InVetValue);
    const [fellowOwnerStateById, setFellowOwnerStateById] = useState<FellowOwnerStateById>({});
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // SignRequest's prepare page can't run inside an iframe (its CSRF cookie is
    // third-party there and gets blocked), so it must open as a top-level tab.
    const prepWindowRef = useRef<Window | null>(null);
    const requestGenerationRef = useRef<number>(0);

    const isStaleRequest = (generation: number): boolean => generation !== requestGenerationRef.current;

    const buildPreparationRequest = (loadedPreview: LoiCoOwnersPreview): LoiPreparationRequest => ({
        selectedRowIds,
        primaryOwnerMode,
        fellowOwners: loadedPreview.fellowOwners
            .filter((fellowOwner) => fellowOwnerStateById[fellowOwner.associateMemberId]?.isSelected)
            .map((fellowOwner) => ({
                associateMemberId: fellowOwner.associateMemberId,
                mode: fellowOwnerStateById[fellowOwner.associateMemberId].mode,
            })),
    });

    const startPreparation = (request: LoiPreparationRequest) => {
        const generation = requestGenerationRef.current;
        setIsLoading(true);
        setPrepareUrl(null);
        setErrorMessage(null);
        fetchLoiPreparationUrl(axios, props.engagementId, props.eventId, props.buyerId, request)
            .then((url) => {
                if (isStaleRequest(generation)) {
                    return;
                }
                setPrepareUrl(url);
                prepWindowRef.current = window.open(url, '_blank');
            })
            .catch((err: unknown) => {
                if (isStaleRequest(generation)) {
                    return;
                }
                setErrorMessage(getPreparationErrorMessage(err));
            })
            .finally(() => {
                if (isStaleRequest(generation)) {
                    return;
                }
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!props.isVisible || !axios) {
            return;
        }
        requestGenerationRef.current += 1;
        const generation = requestGenerationRef.current;
        setPreview(null);
        setPrepareUrl(null);
        setErrorMessage(null);
        setIsLoading(true);
        fetchLoiCoOwnersPreview(axios, props.engagementId, props.eventId, props.buyerId)
            .then((loadedPreview) => {
                if (isStaleRequest(generation)) {
                    return;
                }
                setPreview(loadedPreview);
                setSelectedRowIds(
                    loadedPreview.coOwners
                        .filter((coOwner) => coOwner.issue === LoiCoOwnerIssue.None)
                        .map((coOwner) => coOwner.rowId),
                );
                setPrimaryOwnerMode(loadedPreview.primaryOwner.mode);
                setFellowOwnerStateById(buildFellowOwnerState(loadedPreview.fellowOwners));
                setIsLoading(false);
            })
            .catch(() => {
                if (isStaleRequest(generation)) {
                    return;
                }
                setIsLoading(false);
                setErrorMessage(CO_OWNERS_LOAD_ERROR);
            });
        return () => {
            requestGenerationRef.current += 1;
        };
    }, [props.isVisible, axios, props.engagementId, props.eventId, props.buyerId]);

    useEffect(() => {
        if (!props.isVisible || !axios || !prepareUrl) {
            return;
        }
        let cancelled = false;
        const intervalId = setInterval(async () => {
            if (cancelled) return;
            try {
                const isPrepared = await fetchLoiPreparationStatus(
                    axios,
                    props.engagementId,
                    props.eventId,
                    props.buyerId,
                );
                if (isPrepared && !cancelled) {
                    if (prepWindowRef.current && !prepWindowRef.current.closed) {
                        prepWindowRef.current.close();
                    }
                    createNotification('LOI prepared successfully!', 'success');
                    props.onClose();
                }
            } catch {
            }
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [props.isVisible, axios, prepareUrl, props.engagementId, props.eventId, props.buyerId, props.onClose]);

    const handleOpenTab = () => {
        if (!prepareUrl) {
            return;
        }
        if (prepWindowRef.current && !prepWindowRef.current.closed) {
            prepWindowRef.current.focus();
            return;
        }
        prepWindowRef.current = window.open(prepareUrl, '_blank');
    };

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="loi-preparation-loading">
                    {Helpers.renderTableLoader()}
                </div>
            );
        }
        if (errorMessage) {
            return <div className="loi-preparation-error">{errorMessage}</div>;
        }
        if (prepareUrl) {
            return (
                <div className="loi-preparation-waiting">
                    <p>
                        The LOI preparation page has opened in a new browser
                        tab. Place the signature fields and send the document
                        there.
                    </p>
                    <p>
                        This window will close automatically once preparation
                        is confirmed.
                    </p>
                    <Button color="primary" onClick={handleOpenTab}>
                        Open SignRequest
                    </Button>
                    <p className="loi-preparation-hint">
                        If a new tab didn't open, use the button above.
                    </p>
                </div>
            );
        }
        if (preview) {
            return renderSigners(preview);
        }
        return null;
    };

    const changeFellowOwnerState = (associateMemberId: number, change: Partial<FellowOwnerState>) => {
        setFellowOwnerStateById((current) => ({
            ...current,
            [associateMemberId]: { ...current[associateMemberId], ...change },
        }));
    };

    const setAllFellowOwnersSelected = (fellowOwners: LoiFellowOwner[], isSelected: boolean) => {
        setFellowOwnerStateById((current) => {
            const next = { ...current };
            fellowOwners.forEach((fellowOwner) => {
                next[fellowOwner.associateMemberId] = { ...current[fellowOwner.associateMemberId], isSelected };
            });
            return next;
        });
    };

    const getFellowOwnerColumns = (fellowOwners: LoiFellowOwner[], isLocked: boolean) => {
        const extraData: FellowOwnerExtraData = { fellowOwnerStateById, isLocked };
        const isAllSelected = fellowOwners.every(
            (fellowOwner) => fellowOwnerStateById[fellowOwner.associateMemberId]?.isSelected,
        );
        return [
            {
                dataField: 'associateMemberId',
                text: '',
                headerFormatter: () => (
                    <Input
                        type="checkbox"
                        aria-label="Select all Fellow Practice Owners"
                        checked={isAllSelected}
                        disabled={isLocked}
                        onChange={() => setAllFellowOwnersSelected(fellowOwners, !isAllSelected)}
                    />
                ),
                formatExtraData: extraData,
                formatter: (cell: number, row: LoiFellowOwner, rowIndex: number, extra: FellowOwnerExtraData) => (
                    <Input
                        type="checkbox"
                        aria-label={`Select ${row.name}`}
                        checked={!!extra.fellowOwnerStateById[row.associateMemberId]?.isSelected}
                        disabled={extra.isLocked}
                        onChange={(event) =>
                            changeFellowOwnerState(row.associateMemberId, { isSelected: event.target.checked })
                        }
                    />
                ),
            },
            { dataField: 'name', text: 'Name' },
            { dataField: 'email', text: 'Email' },
            {
                dataField: 'mode',
                text: 'Signs',
                formatExtraData: extraData,
                formatter: (cell: LoiSigningMode, row: LoiFellowOwner, rowIndex: number, extra: FellowOwnerExtraData) => (
                    <div>
                        <Input
                            type="select"
                            bsSize="sm"
                            aria-label={`Signing mode for ${row.name}`}
                            value={extra.fellowOwnerStateById[row.associateMemberId]?.mode}
                            disabled={extra.isLocked || !row.hasAccount}
                            onChange={(event) =>
                                changeFellowOwnerState(row.associateMemberId, { mode: Number(event.target.value) })
                            }>
                            {SIGNING_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Input>
                        {!row.hasAccount && <span className="loi-preparation-hint">No VetValue account</span>}
                    </div>
                ),
            },
        ];
    };

    const renderPrimaryOwner = (loadedPreview: LoiCoOwnersPreview) => (
        <div className="loi-preparation-section">
            <h6>Primary owner</h6>
            <div className="loi-preparation-primary-owner">
                <span>{loadedPreview.primaryOwner.name}</span>
                <Input
                    type="select"
                    bsSize="sm"
                    aria-label="Primary owner signing mode"
                    value={primaryOwnerMode}
                    disabled={loadedPreview.isRequestCreated}
                    onChange={(event) => setPrimaryOwnerMode(Number(event.target.value))}>
                    {SIGNING_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Input>
            </div>
        </div>
    );

    const renderFellowOwners = (loadedPreview: LoiCoOwnersPreview) => {
        if (loadedPreview.fellowOwners.length === 0) {
            return null;
        }
        return (
            <div className="loi-preparation-section">
                <h6>
                    Fellow Practice Owners
                    <InfoTooltip
                        idText="loi-preparation-fellow-owners"
                        text='Fellow Practice Owners set to "In VetValue" sign in VetValue after the primary owner. Those set to "By email" get a SignRequest link and reminders.'
                    />
                </h6>
                <div className="loi-preparation-co-owner-table">
                    <BootstrapTable
                        striped
                        bordered
                        bootstrap4
                        keyField="associateMemberId"
                        data={loadedPreview.fellowOwners}
                        columns={getFellowOwnerColumns(loadedPreview.fellowOwners, loadedPreview.isRequestCreated)}
                    />
                </div>
            </div>
        );
    };

    const toggleRow = (rowId: number) => {
        setSelectedRowIds((current) =>
            current.includes(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId],
        );
    };

    const toggleAllValidRows = (validRowIds: number[], isAllSelected: boolean) => {
        setSelectedRowIds(isAllSelected ? [] : validRowIds);
    };

    const getColumns = (loadedPreview: LoiCoOwnersPreview, validRowIds: number[]) => {
        const isAllSelected = validRowIds.length > 0 && validRowIds.every((id) => selectedRowIds.includes(id));
        const isLocked = loadedPreview.isRequestCreated;
        return [
            {
                dataField: 'rowId',
                text: '',
                headerFormatter: () => (
                    <Input
                        type="checkbox"
                        aria-label="Select all co-owners"
                        checked={isAllSelected}
                        disabled={isLocked || validRowIds.length === 0}
                        onChange={() => toggleAllValidRows(validRowIds, isAllSelected)}
                    />
                ),
                formatExtraData: { selectedRowIds, isLocked },
                formatter: (
                    cell: number,
                    row: LoiCoOwner,
                    rowIndex: number,
                    extraData: { selectedRowIds: number[]; isLocked: boolean },
                ) => (
                    <Input
                        type="checkbox"
                        aria-label={`Select co-owner in row ${row.rowNumber}`}
                        checked={extraData.selectedRowIds.includes(row.rowId)}
                        disabled={extraData.isLocked || row.issue !== LoiCoOwnerIssue.None}
                        onChange={() => toggleRow(row.rowId)}
                    />
                ),
            },
            { dataField: 'rowNumber', text: '#' },
            { dataField: 'name', text: 'Name' },
            { dataField: 'email', text: 'Email' },
            {
                dataField: 'issue',
                text: 'Issue',
                formatter: (cell: LoiCoOwnerIssue) => CO_OWNER_ISSUE_TEXT[cell],
            },
        ];
    };

    const renderCoOwners = (loadedPreview: LoiCoOwnersPreview) => {
        if (!loadedPreview.isTableLinked) {
            return null;
        }
        if (!loadedPreview.isTableFound) {
            return (
                <div className="loi-preparation-error">
                    The owners table linked to the LOI step can't be found. Fix or unlink it in the LOI execution step settings.
                </div>
            );
        }
        const hasIssue = loadedPreview.coOwners.some((coOwner) => coOwner.issue !== LoiCoOwnerIssue.None);
        const validRowIds = loadedPreview.coOwners
            .filter((coOwner) => coOwner.issue === LoiCoOwnerIssue.None)
            .map((coOwner) => coOwner.rowId);
        return (
            <div className="loi-preparation-section">
                <h6>
                    Co-owners
                    <InfoTooltip
                        idText="loi-preparation-co-owners"
                        text="Each selected co-owner will be emailed a SignRequest link and reminders after the primary owner signs."
                    />
                </h6>
                {hasIssue && (
                    <Alert color="warning">
                        Some co-owners can't be selected because their email is missing or incorrect. Fix them in the DaQI, then reopen this pop-up.
                    </Alert>
                )}
                {loadedPreview.coOwners.length > 0 && (
                    <div className="loi-preparation-co-owner-table">
                        <BootstrapTable
                            striped
                            bordered
                            bootstrap4
                            keyField="rowId"
                            data={loadedPreview.coOwners}
                            columns={getColumns(loadedPreview, validRowIds)}
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderSigners = (loadedPreview: LoiCoOwnersPreview) => {
        return (
            <div className="loi-preparation-co-owners">
                {loadedPreview.isRequestCreated && (
                    <p>The SignRequest request already exists, so its signers can't be changed.</p>
                )}
                {renderPrimaryOwner(loadedPreview)}
                {renderFellowOwners(loadedPreview)}
                {renderCoOwners(loadedPreview)}
                <div className="loi-preparation-actions">
                    <Button
                        color="primary"
                        disabled={!loadedPreview.canPrepare}
                        onClick={() => startPreparation(buildPreparationRequest(loadedPreview))}>
                        Continue to SignRequest
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="loi-preparation-modal">
            <Rodal
                visible={props.isVisible}
                onClose={props.onClose}
                className="checkout-modal loi-preparation-rodal"
                width={LOI_PREPARATION_MODAL_WIDTH}
                customStyles={{ maxWidth: '90vw' }}
                animation="fade"
                showMask={true}>
                <ModalHeader>
                    <span>Prepare LOI for Signing</span>
                </ModalHeader>
                <ModalBody>
                    {renderBody()}
                </ModalBody>
            </Rodal>
        </div>
    );
};
